import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import EntityMember from "_shared/entity-members/entity-member";
import Percent from "_shared/percentages/percent";
import { Masked, MaskedFeeSchedule, MaskedTeam, MaskedContracts, MaskedDiscount } from "work/_shared/masked-fields";
import Proposal from "work/proposals/proposal";
import ProposalField, { ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";
import { FeeScheduleRedline } from "work/proposals/redlining/fee-schedule-redline/fee-schedule-redline";
import FieldRedline, { FieldRedlineArray } from "work/proposals/redlining/field-redlines/field-redline";
import ProposalRedline, { RedlinedDates, RedlinedDocuments } from "work/proposals/redlining/proposal-redline";
import WorkDocument from "work/work-agreements/contracts/work-document";
import ProjectDescription from "work/work-agreements/project-description/project-description";
import ProjectName from "work/work-agreements/project-name/project-name";

export default class ProposalRedlineFactory {
  public static fromProposal(proposal: Proposal): ProposalRedline | undefined {
    if (!proposal.id || !proposal.name || !proposal.description) return;

    let originalVersion: Proposal = proposal.clone();
    let revisedVersion: Proposal = originalVersion;
    if (proposal.supersedes && !proposal.isApprovedByBothParties) {
      originalVersion = new Proposal({
        currentUser: proposal.currentUser,
        spec: proposal.supersedes,
        options: {
          withRedline: false,
        },
      });
      revisedVersion = proposal.userIsLatestSubmittingParty ? originalVersion : proposal.clone();
    }

    let currentVersion: Proposal | undefined = proposal.userIsLatestSubmittingParty ? revisedVersion : undefined;
    if (proposal.partyRevision && !currentVersion?.lastUpdated?.isAfter(proposal.partyRevision.lastUpdated)) {
      currentVersion = new Proposal({
        currentUser: proposal.currentUser,
        spec: {
          ...proposal.partyRevision,
        },
        options: {
          withRedline: false,
        },
      });
    }
    if (proposal.userRevision && !currentVersion?.lastUpdated?.isAfter(proposal.userRevision.lastUpdated)) {
      currentVersion = new Proposal({
        currentUser: proposal.currentUser,
        spec: {
          ...proposal.userRevision,
        },
        options: {
          withRedline: false,
        },
      });
    }

    const nameRedline = new FieldRedline<ProjectName>({
      field: ProposalField.Name,
      originalEntry: originalVersion.name ?? null,
      revisedEntry: revisedVersion.name ?? null,
      currentEntry: currentVersion?.unresolvedFields.has(ProposalFieldName.Name) ? undefined : currentVersion?.name,
      diffWords: true,
    });

    const descriptionRedline = new FieldRedline<ProjectDescription>({
      field: ProposalField.Description,
      originalEntry: originalVersion.description ?? null,
      revisedEntry: revisedVersion.description ?? null,
      currentEntry: currentVersion?.unresolvedFields.has(ProposalFieldName.Description)
        ? undefined
        : currentVersion?.description,
      diffWords: true,
    });

    const feeScheduleRedline =
      Masked.isMasked(originalVersion?.feeSchedule) ||
      Masked.isMasked(revisedVersion.feeSchedule) ||
      Masked.isMasked(currentVersion?.feeSchedule)
        ? new MaskedFeeSchedule()
        : new FeeScheduleRedline(originalVersion.feeSchedule, revisedVersion.feeSchedule, currentVersion?.feeSchedule);

    const teamRestrictedRedline =
      Masked.isMasked(originalVersion.teamRestricted) ||
      Masked.isMasked(proposal?.teamRestricted) ||
      Masked.isMasked(currentVersion?.teamRestricted)
        ? new MaskedTeam()
        : new FieldRedline<AHBoolean>({
            field: ProposalField.TeamRestriction,
            originalEntry: originalVersion.teamRestricted ?? null,
            revisedEntry: revisedVersion.teamRestricted ?? null,
            currentEntry: currentVersion?.teamRestricted,
            diffWords: false,
          });

    const conflictsCheckWaivedRedline =
      Masked.isMasked(originalVersion.conflictsCheckWaived) ||
      Masked.isMasked(proposal.conflictsCheckWaived) ||
      Masked.isMasked(currentVersion?.conflictsCheckWaived)
        ? new MaskedContracts()
        : new FieldRedline<AHBoolean>({
            field: ProposalField.WaiveConflictsCheck,
            originalEntry: originalVersion.conflictsCheckWaived,
            revisedEntry: revisedVersion.conflictsCheckWaived,
            currentEntry: currentVersion?.conflictsCheckWaived,
            diffWords: false,
          });

    const waivingConflictsChecks = Boolean(
      Masked.isNotMasked(conflictsCheckWaivedRedline) &&
      conflictsCheckWaivedRedline.isAccepted &&
      conflictsCheckWaivedRedline.currentEntry?.isEqualTo(new AHBoolean(true)),
    );

    const discountRedline =
      Masked.isMasked(originalVersion.discount) ||
      Masked.isMasked(proposal.discount) ||
      Masked.isMasked(currentVersion?.discount)
        ? (originalVersion.discount as MaskedDiscount)
        : new FieldRedline<Percent>({
            field: ProposalField.Discount,
            originalEntry: originalVersion.discount ?? null,
            revisedEntry: revisedVersion.discount ?? null,
            currentEntry: currentVersion?.discount,
            diffWords: false,
          });

    return new ProposalRedline({
      name: nameRedline,
      description: descriptionRedline,
      ...this.createDatesRedline({ proposal, originalVersion, revisedVersion, currentVersion }),
      vendorTeam: this.createTeamRedline({ proposal, originalVersion, revisedVersion, currentVersion }),
      feeSchedule: feeScheduleRedline,
      conflictsCheckWaived: conflictsCheckWaivedRedline,
      teamRestricted: teamRestrictedRedline,
      ...this.createDocumentsRedline({ proposal, originalVersion, revisedVersion, currentVersion, waivingConflictsChecks }),
      discount: discountRedline,
      lastUpdated: new AHDate(),
    });
  }

  private static createDatesRedline(params: {
    proposal: Proposal;
    originalVersion: Proposal;
    revisedVersion: Proposal;
    currentVersion?: Proposal;
  }): RedlinedDates {
    const { proposal, originalVersion, revisedVersion, currentVersion } = params;
    return {
      responseDueBy: new FieldRedline({
        field: ProposalField.ResponseDueBy,
        originalEntry: originalVersion.responseDueBy ?? null,
        revisedEntry: revisedVersion.responseDueBy ?? null,
        currentEntry: !currentVersion?.responseDueBy && !proposal.responseDueBy ? null : currentVersion?.responseDueBy,
        diffWords: false,
      }),
      startDate: new FieldRedline({
        field: ProposalField.StartDate,
        originalEntry: originalVersion.startDate ?? null,
        revisedEntry: revisedVersion.startDate ?? null,
        currentEntry: !currentVersion?.startDate && !proposal.startDate ? null : currentVersion?.startDate,
        diffWords: false,
      }),
      endDate: new FieldRedline({
        field: ProposalField.EndDate,
        originalEntry: originalVersion.endDate ?? null,
        revisedEntry: revisedVersion.endDate ?? null,
        currentEntry: !currentVersion?.endDate && !proposal.endDate ? null : currentVersion?.endDate,
        diffWords: false,
      }),
    };
  }

  private static createTeamRedline(params: {
    proposal: Proposal;
    originalVersion: Proposal;
    revisedVersion: Proposal;
    currentVersion?: Proposal;
  }): FieldRedlineArray<EntityMember> | MaskedTeam {
    const { proposal, originalVersion, revisedVersion, currentVersion } = params;
    return Masked.isMasked(originalVersion.vendorTeam) ||
      Masked.isMasked(revisedVersion.vendorTeam) ||
      Masked.isMasked(currentVersion?.vendorTeam)
      ? new MaskedTeam()
      : FieldRedlineArray.create<EntityMember>({
          field: ProposalField.Team,
          userIsLatestSubmittingParty: proposal.userIsLatestSubmittingParty,
          entryField: ProposalField.TeamMember,
          originalEntries:
            originalVersion.vendorTeam?.members.map((memberParams) => EntityMember.create({ ...memberParams })) ?? [],
          revisedEntries:
            revisedVersion.vendorTeam?.members.map((memberParams) => EntityMember.create({ ...memberParams })) ?? [],
          currentEntries: currentVersion?.unresolvedFields.has(ProposalFieldName.Team)
            ? []
            : (currentVersion?.vendorTeam?.members.map((memberParams) => EntityMember.create({ ...memberParams })) ??
              []),
        });
  }

  private static createDocumentsRedline(params: {
    proposal: Proposal;
    originalVersion: Proposal;
    revisedVersion: Proposal;
    currentVersion?: Proposal;
    waivingConflictsChecks: boolean;
  }): RedlinedDocuments {
    const { proposal, originalVersion, revisedVersion, currentVersion, waivingConflictsChecks } = params;
    const documentsRedline = {
      clientPolicies:
        Masked.isMasked(originalVersion?.clientPolicies) ||
        Masked.isMasked(revisedVersion.clientPolicies) ||
        Masked.isMasked(currentVersion?.clientPolicies)
          ? new MaskedContracts()
          : FieldRedlineArray.create<WorkDocument>({
              field: ProposalField.ClientPolicies,
              userIsLatestSubmittingParty: proposal.userIsLatestSubmittingParty,
              entryField: ProposalField.ClientPolicy,
              originalEntries: originalVersion.clientPolicies as WorkDocument[],
              revisedEntries: revisedVersion.clientPolicies as WorkDocument[],
              currentEntries: currentVersion?.unresolvedFields.has(ProposalFieldName.ClientPolicies)
                ? []
                : (currentVersion?.clientPolicies as WorkDocument[]) ?? [],
            }),
      vendorPolicies:
        Masked.isMasked(originalVersion?.vendorPolicies) ||
        Masked.isMasked(revisedVersion.vendorPolicies) ||
        Masked.isMasked(currentVersion?.vendorPolicies)
          ? new MaskedContracts()
          : FieldRedlineArray.create<WorkDocument>({
              field: ProposalField.VendorPolicies,
              userIsLatestSubmittingParty: proposal.userIsLatestSubmittingParty,
              entryField: ProposalField.VendorPolicy,
              originalEntries: originalVersion.vendorPolicies as WorkDocument[],
              revisedEntries: revisedVersion.vendorPolicies as WorkDocument[],
              currentEntries: currentVersion?.unresolvedFields.has(ProposalFieldName.VendorPolicies)
                ? []
                : (currentVersion?.vendorPolicies as WorkDocument[]) ?? [],
            }),
      conflicts:
        Masked.isMasked(originalVersion?.conflicts) ||
        Masked.isMasked(revisedVersion.conflicts) ||
        Masked.isMasked(currentVersion?.conflicts)
          ? new MaskedContracts()
          : FieldRedlineArray.create<WorkDocument>({
              field: ProposalField.Conflicts,
              userIsLatestSubmittingParty: proposal.userIsLatestSubmittingParty,
              entryField: ProposalField.ConflictsDocument,
              originalEntries: originalVersion.conflicts as WorkDocument[],
              revisedEntries: revisedVersion.conflicts as WorkDocument[],
              currentEntries: currentVersion?.unresolvedFields.has(ProposalFieldName.Conflicts)
                ? []
                : (currentVersion?.conflicts as WorkDocument[]) ?? [],
            }),
    };

    if (waivingConflictsChecks && Masked.isNotMasked(documentsRedline.conflicts)) {
      documentsRedline.conflicts = documentsRedline.conflicts.acceptAll();
    }
    return documentsRedline;
  }
}
